import { LoadingButton } from "@mui/lab";
import { Alert, Box, Button, Checkbox, FormControlLabel, Grid2 as Grid, Stack, Tooltip } from "@mui/material";
import copy from "fast-copy";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AntSwitch, CollapsableHeader, ImageUploadList, MyPaper, ResizableTextField, SectionPaper, TextField } from "../../../components";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { useDeleteAttachmentMutation, useUpdateQuoteMutation, useUploadAttachmentToS3Mutation } from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { Attachment } from "../../../redux/slices/material";
import { CoverLetter, QuoteSliceInterface, updateQuoteSlice } from "../../../redux/slices/quote";
import { AppRoutes } from "../../../router/routes";
import { giveMeFromAddress, preparePayload } from "../utils";

export const QuoteCoverLetter: React.FC<{ quoteId: string, readOnly: boolean, setIsSaved: any, refetch: any }> = ({ quoteId, readOnly, setIsSaved, refetch }) => {

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { quote } = useAppSelector(store => store);
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();

    const [updateQuote, updateQuoteData] = useUpdateQuoteMutation();
    const [uploadImage, uploadImageData] = useUploadAttachmentToS3Mutation();
    const [deleteAttachment, deleteResponse] = useDeleteAttachmentMutation();

    const [state, setState] = React.useState<QuoteSliceInterface>(quote);
    const [upload, setUpload] = React.useState<any>(null);
    const [attachments, setAttachments] = React.useState<Attachment[] | null>(null);

    const giveMeErrorMessage = () => {
        let message = `Oops! Something went wrong, Unable to Update Quote Cover Letter. Try Again Later!`;
        let errorData: any = updateQuoteData.error;
        message = errorData?.data?.internal_error ?? errorData?.data?.title ?? message;
        return message;
    };

    const onDeleteSignature = async (file: Attachment | File) => {
        if ((file as Attachment)?._id) {
            await deleteAttachment({ object_id: quoteId, object_type: "quote", attachment_id: (file as Attachment)?._id })
            setAttachments([]);
        } else {
            setUpload(null);
            setAttachments([]);
        }
    };

    const onUpload = (file: any) => {
        if (file) {
            setUpload(file);
            setAttachments([file])
        }
    };

    const saveItemDetails = async () => {
        let attachments: any[] = [];
        if (upload) {
            await uploadImage({ object_id: quoteId, object_type: "quote", file: upload }).then((res: any) => {
                if (res.data) {
                    const newState = copy(state);
                    attachments = copy(res.data.getSignedURL?.data ?? []);

                    if (attachments.length) {
                        attachments[0].url = URL.createObjectURL(upload);
                        attachments[0].status = "saved";
                    }

                    newState.cover_letter.attachments = res.data.getSignedURL?.data ?? [];
                    dispatch(updateQuoteSlice(newState));
                    setUpload(null);
                } else {
                    console.log('error:', res.error);
                }
            });
        }

        let payload = { ...preparePayload({ ...state, cover_letter: { ...state.cover_letter, attachments: attachments.length > 0 ? attachments : state.cover_letter?.attachments } }) };

        dispatch(openBackdrop('Updating....'));
        updateQuote({ id: quoteId, payload }).then((res: any) => {
            if (res.data) {
                setIsSaved(true);
                dispatch(updateQuoteSlice(state));
                enqueueSnackbar('Quote Cover Letter Updated Successfully!', { variant: "success" });
                setTimeout(() => {
                    refetch();
                    dispatch(closeBackdrop());
                }, 2000);
            } else {
                console.log('error:', res.error)
                enqueueSnackbar(giveMeErrorMessage(), { variant: "error" })
                dispatch(closeBackdrop());
            }
        });

        setSearchParams({});
    };

    const updateQuoteCoverLetter = (payload: { key: keyof CoverLetter, value: never }) => {
        let newState: QuoteSliceInterface = copy(state);
        newState.cover_letter[payload.key] = payload.value;

        if (payload.key === 'fromAddressAs') {
            if (payload.value) {
                newState.cover_letter.fromAddress = giveMeFromAddress(newState);
            } else {
                newState.cover_letter['fromAddress'] = '';
            }
        }

        if (payload.key === 'subjectAsName') {
            if (payload.value) {
                newState.cover_letter['subject'] = newState.name;
            } else {
                newState.cover_letter['subject'] = '';
            }
        }

        setState({ ...state, ...newState });
        setIsSaved(false);
    };

    const onUpdate = (key: keyof CoverLetter, value: string | boolean | undefined) => {
        updateQuoteCoverLetter({ key, value: value as never });
    };

    const onSwitched = (e: React.ChangeEvent<HTMLInputElement>, isChecked: boolean, key: keyof QuoteSliceInterface) => {
        let newState: QuoteSliceInterface = copy(state);
        newState[key] = isChecked as never;
        setState({ ...state, ...newState });
    };

    React.useEffect(() => {
        if (deleteResponse.isLoading) {
            dispatch(openBackdrop("Deleting Signature..."))
            return;
        }

        if (deleteResponse.isError) {
            enqueueSnackbar("Unable to delete Signature.", { variant: "error" })
        }

        if (deleteResponse.isSuccess) {
            enqueueSnackbar("Signature deleted successfully.", { variant: "success" })
            setUpload(null);
            setAttachments([]);
        }

        dispatch(closeBackdrop());
        // eslint-disable-next-line
    }, [deleteResponse.status]);

    React.useEffect(() => {
        setState(quote);
        setAttachments((quote.cover_letter.attachments) as Attachment[]);
        // eslint-disable-next-line
    }, [quote]);

    React.useEffect(() => {
        if (Boolean(searchParams.get("forceSave"))) {
            saveItemDetails();
        };
        // eslint-disable-next-line
    }, [searchParams]);

    React.useEffect(() => {
        setIsSaved(true);
        refetch();
        // eslint-disable-next-line
    }, []);

    return (
        (<Stack spacing={1}>
            {/* Title */}
            <MyPaper padding={0}>
                <CollapsableHeader
                    title={"Cover Letter"}
                    showIcon={false}
                    isExpanded={false}
                    showSwitch={true}
                    switchStatus={state.cover_letter.include}
                    onSwitchChanged={(checked) => onUpdate('include', checked)}
                    disabled={readOnly}
                >
                    <></>
                </CollapsableHeader>
            </MyPaper>

            {/* Cover letter body */}
            {state.cover_letter.include && <Stack sx={{ gap: 1 }}>
                <SectionPaper title="Header" >
                    <Grid container spacing={2}>
                        {/* From Address */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Stack width={"100%"}>
                                <Stack gap={1} alignItems={readOnly ? "normal" : "flex-end"} sx={{ position: 'relative' }}>
                                    <TextField
                                        size="small"
                                        id="quote-cover-letter-from-address"
                                        disabled={state.cover_letter.fromAddressAs ? true : false}
                                        value={state.cover_letter.fromAddress}
                                        label="From Address"
                                        multiline
                                        fullWidth
                                        rows={5}
                                        slotProps={{ inputLabel: { shrink: state.cover_letter.fromAddressAs ? true : undefined } }}
                                        placeholder="Enter from address"
                                        onChange={(e) => onUpdate('fromAddress', e.target.value)}
                                        viewOnly={readOnly}
                                    />
                                    <Box sx={{ position: "absolute", right: "8px", top: "8px" }}>
                                        <Tooltip title={state.include_cover_letter_from ? "Disable to hide in the From Address" : "Enable to show in the From Address"} arrow placement="top">
                                            <AntSwitch disabled={readOnly} sx={{ ml: 2 }} size="small" color="primary" checked={state.include_cover_letter_from} onChange={(e, isChecked: boolean) => onSwitched(e, isChecked, "include_cover_letter_from")} />
                                        </Tooltip>
                                    </Box>
                                </Stack>
                                <FormControlLabel control={<Checkbox
                                    disabled={readOnly}
                                    checked={state.cover_letter.fromAddressAs ? true : false}
                                    onChange={(e, checked) => onUpdate('fromAddressAs', checked ? 'soldBy' : '')}
                                />} label={'Same as Sold By Address'} />
                            </Stack>
                        </Grid>

                        {/* Subject */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 6
                            }}>
                            <Stack width={"100%"}>
                                <Stack gap={1} alignItems={readOnly ? "normal" : "flex-end"} sx={{ position: 'relative' }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        id="quote-cover-letter-subject"
                                        disabled={state.cover_letter.subjectAsName ? true : false}
                                        value={state.cover_letter.subject}
                                        label="Subject"
                                        slotProps={{ inputLabel: { shrink: state.cover_letter.subjectAsName ? true : undefined } }}
                                        placeholder="Enter subject"
                                        onChange={(e) => onUpdate('subject', e.target.value)}
                                        viewOnly={readOnly}
                                    />

                                    <Box sx={{ position: "absolute", right: "8px", top: "12px" }}>
                                        <Tooltip title={state.include_cover_letter_subject ? "Disable to hide in the Subject" : "Enable to show in the Subject"} arrow placement="top">
                                            <AntSwitch disabled={readOnly} sx={{ ml: 2 }} size="small" color="primary" checked={state.include_cover_letter_subject} onChange={(e, isChecked: boolean) => onSwitched(e, isChecked, "include_cover_letter_subject")} />
                                        </Tooltip>
                                    </Box>
                                </Stack>
                                <FormControlLabel control={<Checkbox
                                    disabled={readOnly}
                                    checked={state.cover_letter.subjectAsName ? true : false}
                                    onChange={(e, checked) => onUpdate('subjectAsName', checked)}
                                />} label={'Same as Quote Name'} />
                            </Stack>
                        </Grid>
                    </Grid>
                </SectionPaper>

                <SectionPaper title="Content">
                    <ResizableTextField
                        size="small"
                        id="quote-cover-letter-content"
                        value={state.cover_letter.content}
                        label="Content"
                        multiline
                        fullWidth
                        rows={5}
                        placeholder="Enter content"
                        onChange={(e) => onUpdate('content', e.target.value)}
                        viewOnly={readOnly}
                    />
                </SectionPaper>

                <SectionPaper title="Footer">
                    <Grid container spacing={2}>
                        {/* Regards */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 4
                            }}>
                            <TextField
                                size="small"
                                id="quote-cover-regards"
                                value={state.cover_letter.regards}
                                label="Regards"
                                fullWidth
                                placeholder="Enter regards"
                                onChange={(e) => onUpdate('regards', e.target.value)}
                                viewOnly={readOnly}
                            />
                        </Grid>

                        {/* Name */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 4
                            }}>
                            <TextField
                                size="small"
                                id="quote-cover-name"
                                value={state.cover_letter.name}
                                label="Name"
                                fullWidth
                                placeholder="Enter name"
                                onChange={(e) => onUpdate('name', e.target.value)}
                                viewOnly={readOnly}
                            />
                        </Grid>

                        {/* Designation */}
                        <Grid
                            size={{
                                xs: 12,
                                sm: 4
                            }}>
                            <TextField
                                size="small"
                                id="quote-cover-designation"
                                value={state.cover_letter.designation}
                                label="Designation"
                                fullWidth
                                placeholder="Enter designation"
                                onChange={(e) => onUpdate('designation', e.target.value)}
                                viewOnly={readOnly}
                            />
                        </Grid>
                    </Grid>
                </SectionPaper>

                <SectionPaper title="Signature">
                    <ImageUploadList
                        key={upload ? upload.file_name : 'upload'}
                        title='Upload Signature'
                        disabled={updateQuoteData.isLoading || uploadImageData.isLoading || readOnly}
                        files={attachments ? attachments : []}
                        onDelete={onDeleteSignature}
                        onUpload={onUpload}
                        stack={false}
                        disableCollapse={true}
                        shadow={false}
                        hasReleased={quote?.released || false}
                    />
                </SectionPaper>

                {(updateQuoteData.isError) && <Alert sx={{ mt: 2 }} severity="error">{giveMeErrorMessage()}</Alert>}
            </Stack>}
            {
                !readOnly && <MyPaper padding={0} height={"70px"}>
                    <Box sx={{ paddingX: 2 }}>
                        {/* Alert Box */}

                        <Stack
                            direction={"row"}
                            spacing={1}
                            sx={{
                                alignItems: "center",
                                justifyContent: "center",
                                p: 2
                            }}>
                            {/* Cancel Button */}
                            <Button disabled={updateQuoteData.isLoading || uploadImageData.isLoading} variant="outlined" sx={{ width: 200 }} color="primary" onClick={() => navigate(AppRoutes.allQuickQuotes)} >
                                Cancel
                            </Button>

                            {/* Create Button */}
                            <LoadingButton loading={updateQuoteData.isLoading || uploadImageData.isLoading} variant="contained" sx={{ width: 200 }} color="primary" onClick={saveItemDetails}>
                                {"Save"}
                            </LoadingButton>
                        </Stack>
                    </Box>
                </MyPaper>
            }
        </Stack>)
    );
}